import React, {FC, useContext, useEffect, useState} from 'react'
import s from './Control.module.css'
import Button from '../../Button/Button'
import Input from '../../Input/Input'
import Checkbox from '../../Checkbox/Checkbox'
import InitialStateContext from '../../../context/InitialStateContext'
import { checkCurrencyCode } from '../../../utils/checkCurrencyCode'
import {useTranslation} from '../../../hooks/useTranslation'
import Select from '../../Select/Select'
import { RiskType, riskTypes, segmentCounts, SegmentCountType } from '../../../types/Game'
import {
    AUTOBET_INCREASE_PERCENT_MAX,
    AUTOBET_INCREASE_PERCENT_MIN,
    AUTOBET_LIMIT_MAX,
    AUTOBET_LIMIT_MIN
} from "../Game";
import BalanceContext from '../../../context/BalanceContext'

const validationAutobetLimit = (value: string): string => {
    if (value.split('.')[1]?.length > 6) {
        return (+value).toFixed(6)
    } else if (value.startsWith('0') && !value.includes('.')) {
        return String(+value)
    } else {
        return value
    }
}

const validationAutobetIncrease = (value: string): string => {
    if (value.startsWith('0')) {
        return String(+value)
    }

    return value
}

type AutobetResultMode = 'reset' | 'increase'

const Control: FC<IProps> = ({
    amount,
    setAmount,
    riskType,
    setRiskType,
    segmentsCount,
    setSegmentsCount,
    isAutobet,
    setIsAutobet,
    numberOfRounds,
    setNumberOfRounds,
    increaseOnWin,
    setIncreaseOnWin,
    increaseOnLoss,
    setIncreaseOnLoss,
    stopAutobetOnProfit,
    setStopAutobetOnProfit,
    stopAutobetOnLoss,
    setStopAutobetOnLoss,
    spin,
    isGame,

    runAutobet,
    isAutobetRunning,
}) => {
    const { t } = useTranslation()
    const [initialState] = useContext(InitialStateContext)
    const [balance] = useContext(BalanceContext)

    const changeValue = (
        name: 'amount' | 'riskType' | 'segmentsCount' | 'numberOfRounds' | 'stopAutobetOnProfit' | 'stopAutobetOnLoss' | 'increaseOnWin' | 'increaseOnLoss',
        prevValue: string,
        newValue: string | ((prev: string) => string)
    ) => {
        if (typeof newValue !== 'string') {
            newValue = newValue(prevValue)
        }

        // validate number of rounds
        if (name === 'numberOfRounds' && ((newValue !== '' && +newValue < 1) || (+newValue > 10000))) {
            return
        }

        switch (name) {
            case 'amount' :
                setAmount(newValue)
                localStorage.setItem(process.env.REACT_APP_AMOUNT_KEY as string, newValue)
                break
            case 'riskType':
                setRiskType(newValue as RiskType)
                localStorage.setItem(process.env.REACT_APP_RISK_TYPE_KEY as string, newValue)
                break
            case 'segmentsCount':
                setSegmentsCount(newValue as SegmentCountType)
                localStorage.setItem(process.env.REACT_APP_SEGMENTS_COUNT_KEY as string, newValue)
                break
            case 'numberOfRounds':
                setNumberOfRounds(newValue)
                break
            case 'stopAutobetOnProfit':
                setStopAutobetOnProfit(validationAutobetLimit(newValue))
                break
            case 'stopAutobetOnLoss':
                setStopAutobetOnLoss(validationAutobetLimit(newValue))
                break
            case 'increaseOnWin':
                setIncreaseOnWin(validationAutobetIncrease(newValue))
                break
            case 'increaseOnLoss':
                setIncreaseOnLoss(validationAutobetIncrease(newValue))
                break
        }
    }

    /* Bet amount */
    const divideAmountValidation = (amount: string): string => {
        if (+amount / 2 >= initialState?.limits?.minBet) {
            return (+amount / 2).toFixed(initialState?.currencyPrecision).toString()
        }

        return amount
    }

    const multiplyAmountValidation = (amount: string): string => {
        if (+amount * 2 <= initialState?.limits?.maxBet) {
            return (+amount * 2).toFixed(initialState?.currencyPrecision).toString()
        }

        return amount
    }

    const maxAmountValidation = (amount: string) => {
        if (initialState.limits) {
            const maxBet = initialState?.limits?.maxBet
            const maxAmount = balance <= maxBet ? balance: maxBet
            return maxAmount.toFixed(initialState?.currencyPrecision)
        } else {
            return amount
        }
    }

    const amountActions = [
        { text: '½', action: () => changeValue('amount', amount, divideAmountValidation(amount)) },
        { text: '2×', action: () => changeValue('amount', amount, multiplyAmountValidation(amount)) },
        { text: t('MAX'), action: () => changeValue('amount', amount, maxAmountValidation(amount)) },
    ]
    /* ----------- */

    /* Number Of Rounds */
    const numberOfRoundsActions = [
        { text: '20', action: () => setNumberOfRounds('20') },
        { text: '50', action: () => setNumberOfRounds('50') },
        { text: '100', action: () => setNumberOfRounds('100') },
    ]
    /* ----------- */

    useEffect(() => {
        if (!initialState) return

        const defaultAmount = localStorage.getItem(process.env.REACT_APP_AMOUNT_KEY!)
        const defaultRisk = localStorage.getItem(process.env.REACT_APP_RISK_TYPE_KEY!)
        const defaultSegmentsCount = localStorage.getItem(process.env.REACT_APP_SEGMENTS_COUNT_KEY!)

        setAmount(
            defaultAmount ?? initialState?.limits?.minBet
                .toFixed(initialState?.currencyPrecision)
                .toString()
        )

        if (defaultRisk) {
            setRiskType(defaultRisk as RiskType)
        }

        if (defaultSegmentsCount) {
            setSegmentsCount(defaultSegmentsCount as SegmentCountType)
        }
    }, [initialState])

    const [autobetOnWinMode, setAutobetOnWinMode] = useState<AutobetResultMode>('reset')
    const [autobetOnLossMode, setAutobetOnLossMode] = useState<AutobetResultMode>('reset')

    useEffect(() => {
        if (autobetOnWinMode === 'reset') {
            setIncreaseOnWin('0')
        }
    }, [autobetOnWinMode])

    useEffect(() => {
        if (autobetOnLossMode === 'reset') {
            setIncreaseOnLoss('0')
        }
    }, [autobetOnLossMode])

    return (
        <div className={s.control}>
            {!isAutobet && (
                <Button
                    onClick={() => spin(+amount)}
                    disabled={!Object.keys(initialState).length || isGame}
                    clickSoundExcept
                >
                    {t('BET')}
                </Button>
            )}
            {isAutobet && (
                <Button
                    onClick={runAutobet}
                    disabled={isAutobetRunning}
                    clickSoundExcept
                >
                    {t('STARTAUTOBET')}
                </Button>
            )}

            <Input
                value={amount}
                onChange={v =>
                    changeValue('amount', amount, v)
                }
                label={t('BETAMOUNT')}
                actions={amountActions}
                currency={checkCurrencyCode(initialState?.currencyCode)}
                min={initialState?.limits?.minBet}
                max={initialState?.limits?.maxBet}
                precision={initialState?.currencyPrecision}
                autoReplace={[
                    [/[^.\d]+/g, ""],
                    [/^([^\.]*\.)|\./g, '$1'],
                ]}
                readOnly={isAutobetRunning}
            />

            <div className={s.grid}>
                <Select
                    label={t('RISK')}
                    value={riskType}
                    onChange={v => changeValue('riskType', riskType, v)}
                    disabled={isGame || isAutobetRunning}
                >
                    {Object.entries(riskTypes).map(([value, label]) => (
                        <option value={value} key={value}>{t(label)}</option>
                    ))}
                </Select>
                <Select
                    label={t('SEGMENTS')}
                    value={segmentsCount}
                    onChange={v => changeValue('segmentsCount', segmentsCount, v)}
                    disabled={isGame || isAutobetRunning}
                >
                    {Object.entries(segmentCounts).map(([value, label]) => (
                        <option value={value} key={value}>{label}</option>
                    ))}
                </Select>
            </div>

            <div className={s.autobet}>
                <Input
                    value={numberOfRounds}
                    onChange={value =>
                        changeValue('numberOfRounds', numberOfRounds, value)
                    }
                    label={t('NUMROUNDS')}
                    actions={numberOfRoundsActions}
                    max={10000}
                    autoReplace={[[/[^\d]/ig, '']]}
                    readOnly={isAutobetRunning}
                />
                <Checkbox
                    checked={isAutobet}
                    onChange={setIsAutobet}
                    className={s.checkbox}
                    clickSoundExcept
                >
                    {t('AUTOBET')}
                </Checkbox>
            </div>

            {isAutobet && (
                <>
                    <Input
                        value={increaseOnWin}
                        onChange={v => changeValue('increaseOnWin', increaseOnWin, v)}
                        label={t('ONWIN')}
                        min={AUTOBET_INCREASE_PERCENT_MIN}
                        max={AUTOBET_INCREASE_PERCENT_MAX}
                        autoReplace={[[/[^\d]/ig, '']]}
                        readOnly={isAutobetRunning}
                        switcher={{
                            reset: t('RESET'),
                            increase: t('INCREASEBY'),
                        }}
                        switcherValue={autobetOnWinMode}
                        onChangeSwitcherValue={setAutobetOnWinMode}
                        isPercents
                        disabled={autobetOnWinMode === 'reset'}
                    />

                    <Input
                        value={increaseOnLoss}
                        onChange={v => changeValue('increaseOnLoss', increaseOnLoss, v)}
                        label={t('ONLOSS')}
                        min={AUTOBET_INCREASE_PERCENT_MIN}
                        max={AUTOBET_INCREASE_PERCENT_MAX}
                        autoReplace={[[/[^\d]/ig, '']]}
                        readOnly={isAutobetRunning}
                        switcher={{
                            reset: t('RESET'),
                            increase: t('INCREASEBY'),
                        }}
                        switcherValue={autobetOnLossMode}
                        onChangeSwitcherValue={setAutobetOnLossMode}
                        isPercents
                        disabled={autobetOnLossMode === 'reset'}
                    />

                    <Input
                        value={stopAutobetOnProfit}
                        onChange={v => changeValue('stopAutobetOnProfit', stopAutobetOnProfit, v)}
                        currency={checkCurrencyCode(initialState?.currencyCode)}
                        label={t('STOPONPROFIT')}
                        max={AUTOBET_LIMIT_MAX}
                        autoReplace={[
                            [/[^.\d]+/g, ""],
                            [/^([^\.]*\.)|\./g, '$1'],
                        ]}
                        readOnly={isAutobetRunning}
                    />

                    <Input
                        value={stopAutobetOnLoss}
                        onChange={v => changeValue('stopAutobetOnLoss', stopAutobetOnLoss, v)}
                        currency={checkCurrencyCode(initialState?.currencyCode)}
                        label={t('STOPONLOSS')}
                        max={AUTOBET_LIMIT_MAX}
                        autoReplace={[
                            [/[^.\d]+/g, ""],
                            [/^([^\.]*\.)|\./g, '$1'],
                        ]}
                        readOnly={isAutobetRunning}
                    />
                </>
            )}
        </div>
    )
}

export default Control

interface IProps {
    amount: string
    setAmount: React.Dispatch<React.SetStateAction<string>>
    riskType: RiskType
    setRiskType: React.Dispatch<React.SetStateAction<RiskType>>
    segmentsCount: SegmentCountType
    setSegmentsCount: React.Dispatch<React.SetStateAction<SegmentCountType>>
    isAutobet: boolean
    setIsAutobet: (value: boolean) => void
    increaseOnWin: string
    setIncreaseOnWin: React.Dispatch<React.SetStateAction<string>>
    increaseOnLoss: string
    setIncreaseOnLoss: React.Dispatch<React.SetStateAction<string>>
    numberOfRounds: string
    setNumberOfRounds: React.Dispatch<React.SetStateAction<string>>
    stopAutobetOnProfit: string
    setStopAutobetOnProfit: React.Dispatch<React.SetStateAction<string>>
    stopAutobetOnLoss: string
    setStopAutobetOnLoss: React.Dispatch<React.SetStateAction<string>>
    spin: (amount: number) => void

    runAutobet: () => void
    isAutobetRunning: boolean
    isGame: boolean
}
